import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon, CheckIcon, StarFillIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useMemo, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import SearchInput from 'dashboard/components-lib/SearchInput';
import {
  GOOGLE_SUPPORTED_LANGUAGE,
  GoogleSupportedLanguage,
} from 'dashboard/lib/constants/locales';
import useTranslations, { TranslationRequests } from 'dashboard/lib/hooks/useTranslations';
import { BoundingBox, DocumentGeneric } from 'dashboard/models/document';
import { Translations } from 'dashboard/redux/slices/translations';
import Button from 'design-system/components/Button';
import { ListItem, MenuHeading } from 'design-system/components/Menu';
import { Label } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip/Tooltip';
import defaultTheme from 'design-system/theme';
import useToggle from 'lib/hooks/useToggle';

interface DocumentViewerTranslateDropdownProps {
  document: DocumentGeneric;
  translationLanguage: GoogleSupportedLanguage | null;
  setTranslationLanguage: (translationLanguage: GoogleSupportedLanguage | null) => void;
  setTranslationLoading: (translationLoading: boolean) => void;
  setTranslations: (translatedText: Translations | null) => void;
}

export interface DocumentSearchMatch {
  boundingBox: BoundingBox;
  pageNumber: number;
  id: number;
}

const IconAndLanguageContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const StyledCheckIcon = styled(CheckIcon)`
  margin-right: ${Spacing[1]};
`;

const StyledTranslateIcon = styled(StarFillIcon)`
  margin-right: ${Spacing[1]};
`;

const StyledCaretDown = styled(CaretDownIcon)`
  margin-left: ${Spacing[1]};
`;

const StyledSpan = styled('span')`
  display: flex;
  align-items: center;
`;

const DropdownContainer = styled('div')`
  min-width: 350px;
`;

const TranslateContainer = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 ${Spacing[1]};
`;

const SelectHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${Spacing[1]};
  margin-bottom: ${Spacing[2]};
  color: ${Color.PrimaryBlack};
`;

const TriggerButton = styled(Button)`
  width: max-content;
  min-width: 150px;
  margin: 0 ${Spacing[2]};
  border: 1px solid;
  padding: 0;
  background-color: transparent;
  color: ${Color.PrimaryWhite};
`;

const ResetButton = styled(Button)`
  height: auto;
  width: auto;
  padding: 0;
  background-color: none;
  color: ${Color.CorePurple600};
  &:active(:enabled) {
    background-color: none;
  }
  &:hover:not(:disabled) {
    box-shadow: none;
  }
`;

const SupportedLanguagesContainer = styled('div')`
  scrollbar-gutter: stable;
  overflow: hidden;
  &:hover {
    overflow: auto;
  }
  max-height: 400px;
`;

const DocumentViewerTranslateDropdown = ({
  document,
  setTranslationLoading,
  setTranslations,
  setTranslationLanguage,
  translationLanguage,
}: DocumentViewerTranslateDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openTooltip, { enable: setOpenTooltip, disable: setCloseTooltip }] = useToggle();
  const languageOptions = useMemo(() => {
    return (
      Object.entries(GOOGLE_SUPPORTED_LANGUAGE) as [GoogleSupportedLanguage, string][]
    ).filter(([iso631Code, language]) => {
      if (searchTerm === '') {
        return iso631Code !== translationLanguage;
      } else {
        return (
          iso631Code !== translationLanguage &&
          language.toLowerCase().startsWith(searchTerm.toLowerCase())
        );
      }
    });
  }, [searchTerm, translationLanguage]);
  const { requestTranslations } = useTranslations();
  const translateText = useCallback(
    async (language: GoogleSupportedLanguage) => {
      const textLines = (document.attributes.pages || []).flatMap((page) =>
        page.lines.map((line) => line.text),
      );
      const reqs: TranslationRequests = textLines.map((tl) => [tl, [language]]);
      setTranslationLoading(true);
      const newTranslations = await requestTranslations(reqs);
      setTranslations(newTranslations);
      setTranslationLoading(false);
    },
    [document.attributes.pages, requestTranslations, setTranslationLoading, setTranslations],
  );
  const handleTranslateLanguageValueChange = (language: GoogleSupportedLanguage) => {
    setTranslationLanguage(language);
    translateText(language);
  };
  const resetTranslationLanguage = () => {
    setTranslationLanguage(null);
    setTranslations(null);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Tooltip
        trigger={
          <TriggerButton
            variant='basic'
            id='document-viewer_translate__trigger-button'
            onClick={setOpenTooltip}
          >
            <TranslateContainer>
              <IconAndLanguageContainer>
                <StyledTranslateIcon />
                {(translationLanguage && GOOGLE_SUPPORTED_LANGUAGE[translationLanguage]) ||
                  (document.attributes['detected-language'] &&
                    GOOGLE_SUPPORTED_LANGUAGE[document.attributes['detected-language']]) ||
                  'Translate'}
              </IconAndLanguageContainer>
              <StyledCaretDown />
            </TranslateContainer>
          </TriggerButton>
        }
        interactive={false}
        open={openTooltip}
        onPointerDownOutside={setCloseTooltip}
        onEscapeKeyDown={setCloseTooltip}
        hideArrow
        style={{ maxWidth: 'unset' }}
      >
        <DropdownContainer>
          <SelectHeader>
            <Label size='lg' weight='bold'>
              Choose document language
            </Label>
            <ResetButton
              disabled={translationLanguage == null}
              variant='inverse'
              id='document-viewer_translate__reset-translation'
              onClick={resetTranslationLanguage}
            >
              Reset
            </ResetButton>
          </SelectHeader>
          <SearchInput
            placeholder='Search language'
            value={searchTerm}
            handleInput={setSearchTerm}
            autoFocus
            maxLength={100}
            title={null}
            useClearIcon
          />
          <MenuHeading>Source Language</MenuHeading>
          <ListItem
            id='document-viewer-translate__language-option-source-language'
            onClick={resetTranslationLanguage}
          >
            {(document.attributes['detected-language'] &&
              GOOGLE_SUPPORTED_LANGUAGE[document.attributes['detected-language']]) ||
              'Original Language'}
          </ListItem>
          <MenuHeading>Supported Languages</MenuHeading>
          <SupportedLanguagesContainer>
            {translationLanguage && (
              <ListItem focused>
                <StyledSpan>
                  <StyledCheckIcon />
                  {GOOGLE_SUPPORTED_LANGUAGE[translationLanguage]}
                </StyledSpan>
              </ListItem>
            )}
            {languageOptions.map(([iso631Code, language]) => (
              <ListItem
                key={iso631Code}
                id={`document-viewer__translate-language-option-${iso631Code}`}
                onClick={() => handleTranslateLanguageValueChange(iso631Code)}
              >
                {language}
              </ListItem>
            ))}
          </SupportedLanguagesContainer>
        </DropdownContainer>
      </Tooltip>
    </ThemeProvider>
  );
};

export default DocumentViewerTranslateDropdown;
